import { type ReactElement } from 'react';

import { AllergenBookletPage } from './allergen-booklet/allergen-booklet-page.js';
import { type BespokePageRegistry } from './bespoke-page.js';

/**
 * The same-origin path the panel serves the allergen booklet from.
 *
 * A relative path, never a cross-origin literal, so the frontend holds no address of
 * its own (admin-panel R12.2). In this build it is a committed sample under `public/`;
 * a full build points it at the Menus_Backend's served copy of the current booklet,
 * which is a value change here and not a component one.
 */
const ALLERGEN_BOOKLET_URL = '/allergen-booklet-sample.pdf';

/**
 * The Bespoke_Pages this deployment mounts, keyed `<productKey>:<bespokePageKey>`
 * (admin-panel R2.5, menus-spec R13.1).
 *
 * One entry now: the allergen-booklet page, which shows the source PDF a reviewer checks
 * a queued item against. The combined menu-workspace page is gone — its review-and-edit
 * work is first-class routes (`/products/$productKey/review` and `.../menus`), reached
 * through the operations composed in `pages/menus-wiring.ts` — so no Bespoke_Page
 * mediates it any longer. The Menus_Backend's review router is still mounted by the
 * backend at `/products/uk-fast-food-menus/bespoke/menu-workspace`, which is the relative
 * base those operations resolve against; that mount is the API path, not a UI page, and
 * it stays.
 *
 * The allergen-booklet page composes nothing: it renders the booklet the panel supplies
 * as a same-origin URL (the focused review also shows it in an on-page Dialog). The
 * framework mounts it for the fast-food Product's routes alone; no Declared_Page depends
 * on it.
 */
export const BESPOKE_PAGES: BespokePageRegistry = {
  'uk-fast-food-menus:allergen-booklet': {
    key: 'allergen-booklet',
    component: allergenBookletComponent,
  },
};

function allergenBookletComponent(): ReactElement {
  return <AllergenBookletPage bookletUrl={ALLERGEN_BOOKLET_URL} />;
}
